import { routeTo, selectNavigationState } from 'store/slice/navigation';
import { RootState } from 'store/store.types';
import { MenuWindowState, selectScreenWidth } from 'store/slice/menu';
import { selectConfigFromBootstrap, setCurrency, selectActivityIdFromBootstrap, selectExperimentsFromBootstrap, selectIsWebViewFromBootstrap } from 'store/slice/bootstrap';
import { selectIsAnyOverlayVisible } from 'store/slice/overlay';
import {
    ActivitiesOverlayCurrencyItemClickEventActivitiesFieldsV2,
    ActivitiesOverlayLanguageItemClickEventActivitiesFieldsV2,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesOverlayCurrencyItemClickEventActivitiesManifestV2,
    ActivitiesOverlayLanguageItemClickEventActivitiesManifestV2,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { createSelector, Dispatch } from '@reduxjs/toolkit';
import { Currency, Language, MenuState } from '@agoda/layout';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import * as slices from 'store/slice';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { ScreenSize, selectScreenSize } from 'store/slice/screen';
import { isHomePage } from 'core/constant/analytics/AnalyticsPageType';
import { ExperimentId } from 'core/type/ExperimentId';
import { isB } from 'core/window/bootstrapConfig';
import { selectIsRebrandCartToSaved } from 'core/utils/cart';

export interface StateProps {
    navigationState: ReturnType<typeof slices.navigation>;
    config: BootstrapConfig;
    menuState: MenuState;
    screenWidth: number;
    isAnyOverlayVisible: boolean;
    supportedLanguages: string[];
    activityId: number;
    screenSize: ScreenSize;
    isRebrandCartToSavedEnabled: boolean;
    isHomePage?: boolean;
    isPreserveBackStack?: boolean;
    isAlwaysShowBackButton?: boolean;
}

export interface DispatchProps {
    changeCurrency: (activityId: number, currency: Currency, pathWithCurrency: string) => void;
    changeLanguage: (pathWithLanguage: string) => void;
    changeLanguageOnDetails: (activityId: number, language: Language, pathWithLanguage: string) => void;
}

export const mapStateToProps = createSelector(
    selectNavigationState,
    selectConfigFromBootstrap,
    (state: RootState) => (state.menu.menuState === MenuWindowState.Closed ? MenuState.Closed : MenuState.Open),
    selectScreenWidth,
    selectIsAnyOverlayVisible,
    (state: RootState) => state.details.detailsResponse?.result?.activity?.content?.supportedLanguages?.locale || [],
    selectActivityIdFromBootstrap,
    selectScreenSize,
    selectExperimentsFromBootstrap,
    selectIsWebViewFromBootstrap,
    (navigationState, config, menuState, screenWidth, isAnyOverlayVisible, supportedLanguages, activityId, screenSize, experiments, isWebView): StateProps => {
        return {
            navigationState,
            config,
            menuState,
            screenWidth,
            isAnyOverlayVisible,
            supportedLanguages,
            activityId,
            screenSize,
            isHomePage: isHomePage(config.pageConfig.pageTypeId),
            isPreserveBackStack: isB(ExperimentId.ACT_4977, experiments),
            isAlwaysShowBackButton: isB(ExperimentId.ACT_5120, experiments),
            isRebrandCartToSavedEnabled: selectIsRebrandCartToSaved(config.behavior, isWebView),
        };
    },
);

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    changeCurrency: (activityId: number, currency: Currency, pathWithCurrency: string) => {
        trackElementEvent<ActivitiesOverlayCurrencyItemClickEventActivitiesFieldsV2>(ActivitiesOverlayCurrencyItemClickEventActivitiesManifestV2.ids, {
            selected_filters: currency.code,
            activity_id: activityId,
        });
        dispatch(setCurrency(currency));
        dispatch(routeTo(pathWithCurrency));
    },
    changeLanguage: (pathWithLanguage: string) => {
        dispatch(routeTo(pathWithLanguage));
    },
    changeLanguageOnDetails: (activityId: number, language: Language, pathWithLanguage: string) => {
        trackElementEvent<ActivitiesOverlayLanguageItemClickEventActivitiesFieldsV2>(ActivitiesOverlayLanguageItemClickEventActivitiesManifestV2.ids, {
            selected_filters: language.cultureCode,
            activity_id: activityId,
        });
        dispatch(routeTo(pathWithLanguage));
    },
});
